import { Block } from '@components/Layout';
import { color, font, query, space } from '@design/templateFns';
import { ChevronLeft, ChevronRight } from '@icon/Chevrons';
import useIsMobile from '@util/customHooks/useIsMobile';
import { FC, useRef, useState } from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';

const Noop = () => null;

const DesktopWrapper = styled.div``;

const CarouselNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
`;

const CarouselArrow = styled.div<{ $right?: boolean }>`
  margin-right: ${({ $right }) => ($right ? '1.5px' : '0')};
  margin-left: ${({ $right }) => ($right ? '0' : '1.5px')};
  height: 36px;
  width: 36px;
  border-radius: 50%;
  cursor: pointer;
  border: 1px solid #e5e5e5;
  svg {
    position: relative;
    top: 40%;
    left: 50%;
    transform: translate(-50%, -50%);
    @media screen and (${query.below('tablet')}) {
      top: 50%;
    }
  }
`;

export const ListContainer = styled.div`
  position: relative;
  width: 100%;
  overflow-x: hidden;
  padding-bottom: 16px;
  /* mobile single slide carousel */
  /* larger sizes */
  .slick-list {
    overflow: visible;
  }
  .slick-track {
    display: flex;
    flex-wrap: nowrap;
    z-index: 4;
  }
  .slick-slide {
    // position: relative !important;
    display: flex;
    margin-left: 12px;
    margin-right: 12px;
  }
  .slick-slide > div {
    display: flex;
    flex: 0 0 100%;
  }
  .slick-dots {
    display: flex !important;
    flex-direction: row;
    justify-content: center;
    list-style: none;
    padding-inline-start: 0;
    margin-top: 44px;
  }
  .slick-dots li button {
    display: none;
  }
  .slick-prev {
    position: absolute;
    bottom: 0;
    left: calc(50vw - 60px);
  }
  .slick-next {
    position: absolute;
    bottom: 0;
    right: calc(50vw - 60px);
  }
`;

export const CarouselSlot = styled.span`
  position: relative !important;
`;

const Title = styled.h2`
  ${font('eyebrow-xl')};
  text-transform: uppercase;
  text-align: center;
  padding-bottom: calc(${space('x12')} - ${space('lg')});
  @media screen and (${query.below('tablet')}) {
    padding-bottom: 28px;
  }
`;

const StyledSpacer = styled.div`
  border: 1px solid #e60000;
  width: 28px;
  height: 0px;
  margin: 20px auto;
`;

const TestimonialWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 42px;
`;

const TestimonialBlock = styled.div`
  ${font('body-md')};
  width: 22%;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 155%;
  text-align: center;
  width: 100%;
`;

const TestimonialTitle = styled.h4`
  ${font('subhead-lg')};
  padding: 0;
  @media screen and (${query.below('tablet')}) {
    ${font('subhead-sm')};
  }
`;

const TestimonialText = styled.p`
  ${font('body-md-light')};
  color: ${color('gray5')};
  margin: 16px 0;
  @media screen and (${query.below('tablet')}) {
    ${font('body-sm-light')};
  }
`;

const TestimonialAuthor = styled.p`
  ${font('body-sm-light')};
  color: ${color('gray5')};
`;

const slideShowDesktopLimit = 3;
const slideShowMobileLimit = 1;

export interface ClientTestimonialsProps {
  header: string;
  testimonials: {
    title: string;
    text: string;
    author: string;
  }[];
}

export const ClientTestimonials: FC<ClientTestimonialsProps> = ({ header, testimonials }) => {
  const isMobile = useIsMobile();
  const [isPrevDisabled, setIsPrevDisabled] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(false);

  const ref = useRef({
    slickNext: () => {},
    slickPrev: () => {},
    innerSlider: {
      state: {
        currentSlide: 1,
      },
    },
    state: {
      breakpoint: null,
    },
  });

  const onPrev = () => {
    if (isPrevDisabled) return;

    ref.current.slickPrev();
    const disabled = ref.current?.innerSlider?.state?.currentSlide <= 1 ? true : false;

    setIsPrevDisabled(disabled);
    setIsNextDisabled(false);
  };

  const onNext = () => {
    if (isNextDisabled) return;

    ref.current.slickNext();

    const slideShow =
      ref?.current?.state?.breakpoint != null && ref?.current?.state?.breakpoint <= 480
        ? slideShowMobileLimit
        : slideShowDesktopLimit;
    const currentSlideShow = testimonials.length - 1 - slideShow;

    const disabled =
      ref.current?.innerSlider?.state?.currentSlide >= currentSlideShow ? true : false;

    setIsNextDisabled(disabled);
    setIsPrevDisabled(false);
  };

  //there are always three client testimonials
  const SliderConfig = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: slideShowDesktopLimit,
    arrow: true,
    prevArrow: <Noop />,
    nextArrow: <Noop />,
    easing: 'ease-in-out',
    slidesToScroll: 1,
    variableWidth: false,
    mobileFirst: true,
    responsive: [
      {
        breakpoint: 480,
        settings: {
          slidesToShow: slideShowMobileLimit,
          slidesToScroll: 1,
        },
      },
    ],
  };
  const isNavBarEnable = isMobile
    ? testimonials.length > slideShowMobileLimit
    : testimonials.length > slideShowDesktopLimit;

  return (
    <Block>
      <DesktopWrapper>
        <Title>{header}</Title>
        <TestimonialWrapper>
          <ListContainer ref={ref} as={Slider} {...SliderConfig}>
            {testimonials &&
              testimonials.map(function (testimonial, index) {
                return (
                  <CarouselSlot key={index}>
                    <TestimonialBlock>
                      <TestimonialTitle>{testimonial.title}</TestimonialTitle>
                      <StyledSpacer />
                      <TestimonialText>{testimonial.text}</TestimonialText>
                      <TestimonialAuthor>{testimonial.author}</TestimonialAuthor>
                    </TestimonialBlock>
                  </CarouselSlot>
                );
              })}
          </ListContainer>
        </TestimonialWrapper>
        {isNavBarEnable && (
          <CarouselNav>
            <CarouselArrow onClick={(e) => onPrev()} $right={true}>
              <ChevronLeft color={isPrevDisabled ? '#ddd' : '#383737'} />
            </CarouselArrow>
            <CarouselArrow onClick={(e) => onNext()}>
              <ChevronRight color={isNextDisabled ? '#ddd' : '#383737'} />
            </CarouselArrow>
          </CarouselNav>
        )}
      </DesktopWrapper>
    </Block>
  );
};

export default ClientTestimonials;
